/**
 * ISO 4217 Currency Codes with comprehensive European and international support
 * Source: ISO 4217 Standard, Updated 2024
 */

export interface CurrencyInfo {
  code: string;           // ISO 4217 alpha-3 code
  name: string;           // English name
  localName?: string;     // Local language name
  symbol: string;         // Currency symbol
  minorUnit: number;      // Number of decimal places
  numeric: string;        // ISO 4217 numeric code
  countries: string[];    // Countries using this currency
  isActive: boolean;      // Currently active
  isCommon: boolean;      // Commonly used in invoicing
}

export const CURRENCY_DATA: Record<string, CurrencyInfo> = {
  // European Union and EEA
  EUR: { code: 'EUR', name: 'Euro', symbol: '€', minorUnit: 2, numeric: '978', countries: ['AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK'], isActive: true, isCommon: true },
  
  // European Non-Euro Countries
  BGN: { code: 'BGN', name: 'Bulgarian Lev', localName: 'български лев', symbol: 'лв', minorUnit: 2, numeric: '975', countries: ['BG'], isActive: true, isCommon: true },
  CZK: { code: 'CZK', name: 'Czech Koruna', localName: 'koruna česká', symbol: 'Kč', minorUnit: 2, numeric: '203', countries: ['CZ'], isActive: true, isCommon: true },
  DKK: { code: 'DKK', name: 'Danish Krone', localName: 'dansk krone', symbol: 'kr', minorUnit: 2, numeric: '208', countries: ['DK'], isActive: true, isCommon: true },
  HUF: { code: 'HUF', name: 'Hungarian Forint', localName: 'magyar forint', symbol: 'Ft', minorUnit: 2, numeric: '348', countries: ['HU'], isActive: true, isCommon: true },
  PLN: { code: 'PLN', name: 'Polish Zloty', localName: 'złoty polski', symbol: 'zł', minorUnit: 2, numeric: '985', countries: ['PL'], isActive: true, isCommon: true },
  RON: { code: 'RON', name: 'Romanian Leu', localName: 'leu românesc', symbol: 'lei', minorUnit: 2, numeric: '946', countries: ['RO'], isActive: true, isCommon: true },
  SEK: { code: 'SEK', name: 'Swedish Krona', localName: 'svensk krona', symbol: 'kr', minorUnit: 2, numeric: '752', countries: ['SE'], isActive: true, isCommon: true },
  CHF: { code: 'CHF', name: 'Swiss Franc', localName: 'Schweizer Franken', symbol: 'Fr', minorUnit: 2, numeric: '756', countries: ['CH', 'LI'], isActive: true, isCommon: true },
  GBP: { code: 'GBP', name: 'British Pound Sterling', symbol: '£', minorUnit: 2, numeric: '826', countries: ['GB'], isActive: true, isCommon: true },
  NOK: { code: 'NOK', name: 'Norwegian Krone', localName: 'norsk krone', symbol: 'kr', minorUnit: 2, numeric: '578', countries: ['NO'], isActive: true, isCommon: true },
  
  // Historical European Currency (for transition period/legacy support)
  HRK: { code: 'HRK', name: 'Croatian Kuna', localName: 'hrvatska kuna', symbol: 'kn', minorUnit: 2, numeric: '191', countries: ['HR'], isActive: true, isCommon: false },
  
  // Major International Currencies
  USD: { code: 'USD', name: 'US Dollar', symbol: '$', minorUnit: 2, numeric: '840', countries: ['US'], isActive: true, isCommon: true },
  CAD: { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', minorUnit: 2, numeric: '124', countries: ['CA'], isActive: true, isCommon: true },
  JPY: { code: 'JPY', name: 'Japanese Yen', localName: '日本円', symbol: '¥', minorUnit: 0, numeric: '392', countries: ['JP'], isActive: true, isCommon: true },
  CNY: { code: 'CNY', name: 'Chinese Yuan', localName: '人民币', symbol: '¥', minorUnit: 2, numeric: '156', countries: ['CN'], isActive: true, isCommon: true },
  KRW: { code: 'KRW', name: 'South Korean Won', localName: '대한민국 원', symbol: '₩', minorUnit: 0, numeric: '410', countries: ['KR'], isActive: true, isCommon: true },
  AUD: { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', minorUnit: 2, numeric: '036', countries: ['AU'], isActive: true, isCommon: true },
  NZD: { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', minorUnit: 2, numeric: '554', countries: ['NZ'], isActive: true, isCommon: true },
  SGD: { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', minorUnit: 2, numeric: '702', countries: ['SG'], isActive: true, isCommon: true },
  HKD: { code: 'HKD', name: 'Hong Kong Dollar', localName: '港元', symbol: 'HK$', minorUnit: 2, numeric: '344', countries: ['HK'], isActive: true, isCommon: true },
  
  // Other European Currencies
  ALL: { code: 'ALL', name: 'Albanian Lek', localName: 'lek shqiptar', symbol: 'L', minorUnit: 2, numeric: '008', countries: ['AL'], isActive: true, isCommon: false },
  BAM: { code: 'BAM', name: 'Bosnia and Herzegovina Convertible Mark', symbol: 'КМ', minorUnit: 2, numeric: '977', countries: ['BA'], isActive: true, isCommon: false },
  BYN: { code: 'BYN', name: 'Belarusian Ruble', localName: 'беларуски рубель', symbol: 'Br', minorUnit: 2, numeric: '933', countries: ['BY'], isActive: true, isCommon: false },
  GEL: { code: 'GEL', name: 'Georgian Lari', localName: 'ლარი', symbol: '₾', minorUnit: 2, numeric: '981', countries: ['GE'], isActive: true, isCommon: false },
  ISK: { code: 'ISK', name: 'Icelandic Krona', localName: 'íslenska króna', symbol: 'kr', minorUnit: 0, numeric: '352', countries: ['IS'], isActive: true, isCommon: false },
  MKD: { code: 'MKD', name: 'Macedonian Denar', localName: 'македонски денар', symbol: 'ден', minorUnit: 2, numeric: '807', countries: ['MK'], isActive: true, isCommon: false },
  MDL: { code: 'MDL', name: 'Moldovan Leu', localName: 'leu moldovenesc', symbol: 'L', minorUnit: 2, numeric: '498', countries: ['MD'], isActive: true, isCommon: false },
  RSD: { code: 'RSD', name: 'Serbian Dinar', localName: 'српски динар', symbol: 'дин', minorUnit: 2, numeric: '941', countries: ['RS'], isActive: true, isCommon: false },
  RUB: { code: 'RUB', name: 'Russian Ruble', localName: 'российский рубль', symbol: '₽', minorUnit: 2, numeric: '643', countries: ['RU'], isActive: true, isCommon: false },
  TRY: { code: 'TRY', name: 'Turkish Lira', localName: 'Türk lirası', symbol: '₺', minorUnit: 2, numeric: '949', countries: ['TR'], isActive: true, isCommon: false },
  UAH: { code: 'UAH', name: 'Ukrainian Hryvnia', localName: 'українська гривня', symbol: '₴', minorUnit: 2, numeric: '980', countries: ['UA'], isActive: true, isCommon: false },
  
  // Other International Currencies
  INR: { code: 'INR', name: 'Indian Rupee', localName: 'भारतीय रुपया', symbol: '₹', minorUnit: 2, numeric: '356', countries: ['IN'], isActive: true, isCommon: true },
  BRL: { code: 'BRL', name: 'Brazilian Real', localName: 'real brasileiro', symbol: 'R$', minorUnit: 2, numeric: '986', countries: ['BR'], isActive: true, isCommon: true },
  MXN: { code: 'MXN', name: 'Mexican Peso', localName: 'peso mexicano', symbol: '$', minorUnit: 2, numeric: '484', countries: ['MX'], isActive: true, isCommon: true },
  
  // Middle East and Africa
  AED: { code: 'AED', name: 'UAE Dirham', localName: 'درهم إماراتي', symbol: 'د.إ', minorUnit: 2, numeric: '784', countries: ['AE'], isActive: true, isCommon: true },
  SAR: { code: 'SAR', name: 'Saudi Riyal', localName: 'ريال سعودي', symbol: 'ر.س', minorUnit: 2, numeric: '682', countries: ['SA'], isActive: true, isCommon: true },
  QAR: { code: 'QAR', name: 'Qatari Riyal', localName: 'ريال قطري', symbol: 'ر.ق', minorUnit: 2, numeric: '634', countries: ['QA'], isActive: true, isCommon: true },
  EGP: { code: 'EGP', name: 'Egyptian Pound', localName: 'جنيه مصري', symbol: 'ج.م', minorUnit: 2, numeric: '818', countries: ['EG'], isActive: true, isCommon: true },
  ILS: { code: 'ILS', name: 'Israeli Shekel', localName: 'שקל ישראלי', symbol: '₪', minorUnit: 2, numeric: '376', countries: ['IL'], isActive: true, isCommon: true },
  ZAR: { code: 'ZAR', name: 'South African Rand', symbol: 'R', minorUnit: 2, numeric: '710', countries: ['ZA'], isActive: true, isCommon: true },
};

// Common currencies for prioritized display
export const COMMON_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'DKK', 'SEK', 'NOK', 'PLN', 'CZK'] as const;

// Regional currency groups
export const CURRENCY_REGIONS = {
  'Europe (Euro)': ['EUR'],
  'Europe (Non-Euro)': ['BGN', 'CZK', 'DKK', 'HUF', 'PLN', 'RON', 'SEK', 'CHF', 'GBP', 'NOK', 'ALL', 'BAM', 'BYN', 'GEL', 'ISK', 'MKD', 'MDL', 'RSD', 'RUB', 'TRY', 'UAH'],
  'Americas': ['USD', 'CAD', 'BRL', 'MXN'],
  'Asia-Pacific': ['JPY', 'CNY', 'KRW', 'AUD', 'NZD', 'SGD', 'HKD', 'INR'],
  'Middle East & Africa': ['AED', 'SAR', 'QAR', 'EGP', 'ILS', 'ZAR']
} as const;

// Get all active currency codes
export const getAllCurrencyCodes = (): string[] => {
  return Object.keys(CURRENCY_DATA).filter(code => CURRENCY_DATA[code].isActive);
};

// Get currency info by code
export const getCurrencyInfo = (code: string): CurrencyInfo | undefined => {
  return CURRENCY_DATA[code.toUpperCase()];
};

// Get currency name by code
export const getCurrencyName = (code: string): string => {
  const currency = getCurrencyInfo(code);
  return currency?.name || code;
};

// Get currency symbol by code
export const getCurrencySymbol = (code: string): string => {
  const currency = getCurrencyInfo(code);
  return currency?.symbol || code;
};

// Get formatted currency display (code + name)
export const getFormattedCurrencyDisplay = (code: string): string => {
  const currency = getCurrencyInfo(code);
  return currency ? `${code} - ${currency.name}` : code;
};

// Get formatted currency display with symbol
export const getFormattedCurrencyDisplayWithSymbol = (code: string): string => {
  const currency = getCurrencyInfo(code);
  return currency ? `${code} - ${currency.name} (${currency.symbol})` : code;
};

// Get formatted currency display in German
export const getFormattedCurrencyDisplayGerman = (code: string): string => {
  const currency = getCurrencyInfo(code);
  return currency ? `${code} - ${currency.localName || currency.name}` : code;
};

// Format amount with currency
export const formatAmountWithCurrency = (amount: number, currencyCode: string, locale: string = 'en-US'): string => {
  const currency = getCurrencyInfo(currencyCode);
  if (!currency) return `${amount} ${currencyCode}`;
  
  const formattedAmount = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: currency.minorUnit,
    maximumFractionDigits: currency.minorUnit,
  }).format(amount);
  
  return formattedAmount;
};

// Search currencies by name or code
export const searchCurrencies = (query: string): CurrencyInfo[] => {
  const searchTerm = query.toLowerCase();
  return Object.values(CURRENCY_DATA)
    .filter(currency => 
      currency.isActive && (
        currency.name.toLowerCase().includes(searchTerm) ||
        currency.localName?.toLowerCase().includes(searchTerm) ||
        currency.code.toLowerCase().includes(searchTerm)
      )
    )
    .sort((a, b) => {
      // Prioritize exact matches
      const aExact = a.code.toLowerCase() === searchTerm;
      const bExact = b.code.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize common currencies
      const aCommon = a.isCommon;
      const bCommon = b.isCommon;
      
      if (aCommon && !bCommon) return -1;
      if (!aCommon && bCommon) return 1;
      
      // Finally alphabetical
      return a.name.localeCompare(b.name);
    });
};

// Get currencies by region
export const getCurrenciesByRegion = (region: keyof typeof CURRENCY_REGIONS): CurrencyInfo[] => {
  const currencyCodes = CURRENCY_REGIONS[region];
  return currencyCodes.map(code => CURRENCY_DATA[code]).filter(Boolean);
};

export type CommonCurrency = typeof COMMON_CURRENCIES[number];
export type AllCurrency = keyof typeof CURRENCY_DATA;
export type CurrencyRegion = keyof typeof CURRENCY_REGIONS;